import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLang } from "../../contexts/LanguageContext";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { CalendarClock, ArrowRight, Info } from "lucide-react";
import { toast } from "sonner";
import { BackBar } from "../../components/BackBar";

const DAYS = [
  { key: "monday", fr: "Lundi", en: "Monday" },
  { key: "tuesday", fr: "Mardi", en: "Tuesday" },
  { key: "wednesday", fr: "Mercredi", en: "Wednesday" },
  { key: "thursday", fr: "Jeudi", en: "Thursday" },
  { key: "friday", fr: "Vendredi", en: "Friday" },
];

export default function AvailabilityPage() {
  const { id } = useParams();   // enrollment id
  const { lang } = useLang();
  const nav = useNavigate();

  const [enrollment, setEnrollment] = useState(null);
  const [form, setForm] = useState({ monday: "", tuesday: "", wednesday: "", thursday: "", friday: "", notes: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/enrollments/${id}`)
      .then(r => {
        setEnrollment(r.data);
        if (r.data.availability) setForm({ ...form, ...r.data.availability });
      })
      .catch(e => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
    // eslint-disable-next-line
  }, [id]);

  const studentName = enrollment?.student
    ? `${enrollment.student.first_name} ${enrollment.student.last_name}`
    : (lang === "fr" ? "votre enfant" : "your child");
  const grade = enrollment?.student?.grade || "";

  const submit = async () => {
    const hasAny = DAYS.some(d => (form[d.key] || "").trim().length > 0);
    if (!hasAny) {
      toast.error(lang === "fr"
        ? "Indiquez au moins un jour de disponibilité."
        : "Please fill at least one day.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/enrollments/${id}/availability`, form);
      toast.success(lang === "fr" ? "Disponibilités enregistrées" : "Availability saved");
      nav("/parent?welcome=1");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-10" data-testid="availability-page">
      <BackBar to="/parent" />

      <div className="mb-6">
        <div className="flex items-center gap-2 text-accent text-xs uppercase tracking-[0.18em] font-bold mb-2">
          <CalendarClock className="w-4 h-4" />
          {lang === "fr" ? "Dernière étape" : "Final step"}
        </div>
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-3">
          {lang === "fr" ? "Disponibilités hebdomadaires" : "Weekly availability"}
        </h1>
        <p className="text-muted-foreground">
          {lang === "fr"
            ? `Indiquez les créneaux pendant lesquels ${studentName}${grade ? ` (${grade})` : ""} est disponible pour suivre les cours. Notre équipe pédagogique construira l'emploi du temps en fonction des disponibilités de tous les élèves de son niveau.`
            : `Tell us when ${studentName}${grade ? ` (${grade})` : ""} is available for classes. Our team will build the timetable to fit all students in that grade.`}
        </p>
      </div>

      {/* Helpful tip */}
      <Card className="mb-6 bg-secondary/50 border-secondary">
        <CardContent className="p-4 flex gap-3 items-start text-sm">
          <Info className="w-5 h-5 text-accent shrink-0 mt-0.5" />
          <div className="space-y-1.5 text-foreground/80">
            <div><strong>{lang === "fr" ? "Indications utiles :" : "Tips:"}</strong></div>
            <ul className="list-disc ml-5 space-y-1 text-xs">
              <li>{lang === "fr"
                ? "En primaire (GS–CM2) : pas de cours le mercredi → mercredi très ouvert."
                : "Primary (GS–CM2): no school Wednesday → Wednesday very open."}</li>
              <li>{lang === "fr"
                ? "Au collège/lycée : cours mercredi matin, après-midi souvent libre."
                : "Middle/high school: classes Wednesday morning, afternoon often free."}</li>
              <li>{lang === "fr"
                ? "En général : les autres jours, les cours OFA ont lieu après 16h30."
                : "Generally: on other days OFA classes happen after 4:30 pm."}</li>
              <li>{lang === "fr"
                ? "Format libre : ex. « 17h–19h », « disponible toute la journée », « pas dispo »."
                : "Free format: e.g. \"5–7 pm\", \"all day available\", \"not available\"."}</li>
            </ul>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base font-medium">
            {lang === "fr" ? "Du lundi au vendredi" : "Monday to Friday"}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {DAYS.map((d) => (
            <div key={d.key} className="grid grid-cols-1 md:grid-cols-[120px_1fr] gap-3 items-start">
              <label className="text-sm font-medium pt-2 text-foreground/80">
                {lang === "fr" ? d.fr : d.en}
              </label>
              <Textarea
                data-testid={`avail-${d.key}`}
                rows={2}
                placeholder={lang === "fr"
                  ? d.key === "wednesday"
                    ? "ex. toute la journée disponible"
                    : "ex. 17h–19h, ou « pas disponible »"
                  : d.key === "wednesday"
                    ? "e.g. all day available"
                    : "e.g. 5 pm – 7 pm, or \"not available\""}
                value={form[d.key]}
                onChange={(e) => setForm(f => ({ ...f, [d.key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="pt-2 border-t border-border/60">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-bold mb-2 block">
              {lang === "fr" ? "Notes complémentaires (facultatif)" : "Additional notes (optional)"}
            </label>
            <Textarea
              data-testid="avail-notes"
              rows={2}
              placeholder={lang === "fr"
                ? "ex. mon enfant a une activité régulière le mercredi à 14h."
                : "e.g. my child has a regular activity on Wednesday at 2pm."}
              value={form.notes}
              onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end mt-6">
        <Button
          onClick={submit}
          disabled={saving}
          data-testid="submit-availability"
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6 shadow-md"
        >
          {saving
            ? (lang === "fr" ? "Enregistrement…" : "Saving…")
            : (lang === "fr" ? "Valider et entrer dans la Vie scolaire" : "Confirm and enter Student Life")}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
